import { useCallback, useMemo } from 'react'
import { HAPTIC_PATTERNS } from '../utils/hapticPatterns'

/**
 * Return type for useHaptic hook
 */
export interface UseHapticReturn {
  /**
   * Trigger vibration with custom pattern
   * @param pattern - Single duration or array of durations in ms
   */
  vibrate: (pattern?: number | readonly number[]) => void

  /**
   * Whether Vibration API is supported
   */
  isSupported: boolean

  /**
   * Quick light vibration (10ms)
   */
  light: () => void

  /**
   * Medium vibration (20ms)
   */
  medium: () => void

  /**
   * Heavy vibration (30ms)
   */
  heavy: () => void

  /**
   * Success pattern (short-pause-short)
   */
  success: () => void

  /**
   * Error pattern (heavy-pause-heavy)
   */
  error: () => void
}

/**
 * Hook for haptic feedback using Vibration API
 *
 * Features:
 * - Detects Vibration API support
 * - Respects prefers-reduced-motion user preference
 * - Provides predefined patterns
 * - Graceful degradation on unsupported devices
 *
 * @example
 * ```tsx
 * const { light, medium, heavy, isSupported } = useHaptic()
 *
 * const handleButtonPress = () => {
 *   light() // Quick tap feedback
 * }
 *
 * const handleLongPress = () => {
 *   heavy() // Long press feedback
 * }
 * ```
 */
export function useHaptic(): UseHapticReturn {
  // Check if Vibration API is supported
  const isSupported = useMemo(() => {
    return typeof navigator !== 'undefined' && 'vibrate' in navigator
  }, [])

  // Check if user prefers reduced motion
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  /**
   * Trigger vibration with given pattern
   */
  const vibrate = useCallback(
    (pattern?: number | readonly number[]) => {
      // Don't vibrate if not supported, reduced motion is preferred, or no pattern
      if (!isSupported || prefersReducedMotion || !pattern) {
        return
      }

      try {
        // Spread readonly arrays to mutable for Vibration API
        const vibratePattern = typeof pattern === 'number' ? pattern : [...pattern]
        navigator.vibrate(vibratePattern)
      } catch (error) {
        // Silently fail - vibration is a nice-to-have feature
        console.debug('Haptic vibration failed:', error)
      }
    },
    [isSupported, prefersReducedMotion]
  )

  /**
   * Light vibration (quick tap feedback)
   */
  const light = useCallback(() => {
    vibrate(HAPTIC_PATTERNS.light)
  }, [vibrate])

  /**
   * Medium vibration (button press feedback)
   */
  const medium = useCallback(() => {
    vibrate(HAPTIC_PATTERNS.medium)
  }, [vibrate])

  /**
   * Heavy vibration (long press trigger)
   */
  const heavy = useCallback(() => {
    vibrate(HAPTIC_PATTERNS.heavy)
  }, [vibrate])

  /**
   * Success vibration pattern
   */
  const success = useCallback(() => {
    vibrate(HAPTIC_PATTERNS.success)
  }, [vibrate])

  /**
   * Error vibration pattern
   */
  const error = useCallback(() => {
    vibrate(HAPTIC_PATTERNS.error)
  }, [vibrate])

  return {
    vibrate,
    isSupported,
    light,
    medium,
    heavy,
    success,
    error,
  }
}
